import React, { forwardRef, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import Grid from "@mui/material/Grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { useAppSelector } from "app/hooks";
import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import DateStringFormField from "components/date-string-form-field";
import InputFormField from "components/input-form-field";
import RadioGroupFormField from "components/radio-group-form-field";
import TimePicker from "components/time-picker";
import {
  COMMUNICATION_METHOD_EMAIL,
  COMMUNICATION_METHOD_VOICE_CALL,
  COMMUNICATION_METHOD_VOICE_DIRECT_MAIL
} from "constants/patients";
import { selectCurrentClinic } from "features/auth/auth-slice";
import type { Patient } from "services/types/patients.type";

import PatientFieldWithSearch from "../patient-field-with-search";
import { NewTouchpointFormValues } from "./new-touchpoint-form";
import { getCommunicationMethodOptions } from "./touchpoint-form-content.utils";
import TouchpointMessageField, {
  TouchpointMessageFieldRef
} from "./touchpoint-message-field.component";

type TouchpointFormContentProps = {
  defaultPatient: Patient | null;
  showMessageField: boolean;
  onPatientSelected?: () => void;
  onEmailSubjectChanged?: () => void;
};

const TouchpointFormContent = forwardRef<
  TouchpointMessageFieldRef,
  TouchpointFormContentProps
>(
  (
    {
      defaultPatient,
      showMessageField,
      onPatientSelected,
      onEmailSubjectChanged
    },
    ref
  ) => {
  const currentClinic = useAppSelector(selectCurrentClinic);

  const { control, setValue, watch } =
    useFormContext<NewTouchpointFormValues>();

  const [selectedPatient, communicationMethod] = watch([
    "patient",
    "communication_method"
  ]);

  const isVoiceCallOrDirectMail =
    Number(communicationMethod) === COMMUNICATION_METHOD_VOICE_CALL ||
    Number(communicationMethod) === COMMUNICATION_METHOD_VOICE_DIRECT_MAIL;

  const isEmail = Number(communicationMethod) === COMMUNICATION_METHOD_EMAIL;

  const communicationMethodOptions = useMemo(
    () => getCommunicationMethodOptions(selectedPatient),
    [selectedPatient]
  );

  useEffect(() => {
    if (selectedPatient) {
      const preferredValue = communicationMethodOptions?.find(
        (option) =>
          Number(option.value) ===
            Number(selectedPatient?.preferred_communication_method) &&
          !option.disabled
      )?.value;

      const firstNotDisabledValue =
        communicationMethodOptions?.find((option) => !option.disabled)?.value ||
        null;

      setValue("communication_method", preferredValue || firstNotDisabledValue);
    } else {
      setValue("assigned_to", null);
      setValue("communication_method", null);
    }
  }, [setValue, selectedPatient, communicationMethodOptions]);

  useEffect(() => {
    if (isVoiceCallOrDirectMail && selectedPatient?.preferred_provider) {
      setValue("assigned_to", {
        label: selectedPatient?.preferred_provider?.fullName,
        value: String(selectedPatient?.preferred_provider?.id)
      });
    } else {
      setValue("assigned_to", null);
    }
  }, [isVoiceCallOrDirectMail, setValue, communicationMethod, selectedPatient]);

  return (
    <Grid container width="100%" gap={3}>
      {!defaultPatient && (
        <Grid item xs={12}>
          <PatientFieldWithSearch
            control={control}
            preferredClinic={currentClinic?.id}
            required
            onOptionSelected={onPatientSelected}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <RadioGroupFormField
          name="communication_method"
          label="Communication Method"
          row
          options={communicationMethodOptions}
          control={control}
          sx={{ mt: 0, mb: -1 }}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <SelectProvider
          isActive
          name="assigned_to"
          label="Assigned To"
          ignoreRegionsFilter
          ignoreClinicsFilter
          includeOtherClinics
          control={control}
          sx={{ ml: 0 }}
          required={isVoiceCallOrDirectMail}
        />
      </Grid>
      <Grid item xs={4}>
        <DateStringFormField
          name="execution_date"
          label="Scheduled Date"
          control={control}
          required
          sx={{ ml: 0, mt: 0 }}
        />
      </Grid>
      <Grid item xs={2}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <TimePicker
            name="execution_time"
            label="Time *"
            control={control as any}
            openPicker={false}
          />
        </LocalizationProvider>
      </Grid>
      {isEmail && (
        <Grid item xs={12}>
          <InputFormField
            name="email_subject"
            label="Email Subject"
            control={control}
            sx={{ ml: 0 }}
            afterUpdate={onEmailSubjectChanged}
          />
        </Grid>
      )}
      {showMessageField && (
        <Grid item xs={12}>
          <TouchpointMessageField
            ref={ref}
            control={control}
            communicationMethod={communicationMethod}
            height={320}
            onMessageUpdate={(html) => {
              setValue("message", html, { shouldDirty: true });
            }}
            onSubjectUpdate={(subject) => {
              setValue("email_subject", subject, {
                shouldDirty: true,
                shouldValidate: true
              });
              onEmailSubjectChanged?.();
            }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <InputFormField
          name="notes"
          label="Notes"
          control={control}
          multiline
          sx={{ ml: 0 }}
        />
      </Grid>
    </Grid>
  );
});

TouchpointFormContent.displayName = "TouchpointFormContent";

export default TouchpointFormContent;
